import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createDetector,
  deleteDetector,
  fetchDetailDetector,
  fetchDetectorList,
  updateDetector,
} from '@/domains/asset-management/apis/detector-management';
import type {
  CreateDetectorPayload,
  CreateDetectorResponse,
  DeleteDetectorResponse,
  DetectorDetailResponse,
  DetectorID,
  DetectorListResponse,
  UpdateDetectorParams,
  UpdateDetectorPayload,
  UpdateDetectorResponse,
  UseDetectorManagementProps,
} from '@/domains/asset-management/types/detector';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

const useDetectorManagement = ({
  dtID = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
}: UseDetectorManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: detectorListData } = useQuery<DetectorListResponse>({
    queryKey: ['detectorListData', queryParams],
    queryFn: () => fetchDetectorList(queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const { data: detailDetectorData, refetch: refetchDetailDetector } = useQuery<
    DetectorDetailResponse,
    Error
  >({
    queryKey: ['detailDetectorData', dtID],
    queryFn: () => fetchDetailDetector(dtID as DetectorID),
    retry: false,
    enabled: !!dtID,
  });

  const createDetectorMutation = useMutation<CreateDetectorResponse, Error, CreateDetectorPayload>({
    mutationFn: (detectorData) => createDetector(detectorData),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['detectorListData', queryParams],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const deleteDetectorMutation = useMutation<DeleteDetectorResponse, Error, DetectorID>({
    mutationFn: (dtID) => deleteDetector(dtID),
    onSuccess: () => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({
            queryKey: ['detectorListData', queryParams],
          });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  const updateDetectorMutation = useMutation<
    UpdateDetectorResponse,
    Error,
    {
      detectorData: UpdateDetectorPayload;
      params: UpdateDetectorParams;
    }
  >({
    mutationFn: ({ detectorData, params }) => updateDetector(detectorData, params),
    onSuccess: (responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['detectorListData', queryParams],
          });
          queryClient.invalidateQueries({
            queryKey: ['detailDetectorData', responseData.data],
          });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(message));
    },
  });

  return {
    detectorListData,
    detailDetectorData,
    refetchDetailDetector,
    createDetector: createDetectorMutation.mutate,
    updateDetector: updateDetectorMutation.mutate,
    deleteDetector: deleteDetectorMutation.mutate,
  };
};

export default useDetectorManagement;
